/**
 * The week, before it happens.
 *
 * Tee times, foursomes, who is playing whom, what everyone is getting and — the
 * part worth printing — exactly which holes each player pops on.
 *
 * Two rounds are not like the others. Quota spends the handicap up front as a
 * starting points total and pops on no hole at all, and the championship is not
 * drawn but seeded off the standings, so its foursomes are a projection until
 * the week is played.
 *
 * That last column is not decoration. Stroke index is the one field where a
 * mistake is invisible and wrong all week, and the only practical way to catch
 * a transposed one is to put the pop holes in front of twelve people who know
 * the course. If Phil is popping on the 6th and everyone knows the 6th is the
 * easiest hole out there, the card is wrong and this is where that surfaces.
 */

import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { clockTime } from '../engine/clock'
import { holesFor, quotaTarget } from '../engine/handicap'
import { hasSeedingBasis, type Standings } from '../engine/leaderboard'
import { formatName, matchNumber } from '../engine/naming'
import { compass, dayFor, hourFor, longWeather, type ForecastDay } from '../engine/weather'
import { SkyGlyph, WindArrow } from './WeatherGlyphs'
import { matchCounts } from '../engine/types'
import type { Tournament } from '../engine/types'
import {
  buildContext,
  computeAll,
  contestOrder,
  courseOf,
  defaultOpenDays,
  playerName,
  roundDay,
  inPlayOrder,
  roundDays,
  todayLocal,
  type OutstandingCard,
  type TournamentView,
} from '../select'
import { SeedingBlocked, seedingBlockers } from './Unsigned'
import { Yearbook } from './Yearbook'
import { claimMassage, fetchWeather, mergedConfirmations, mergedScores, useStore } from '../store'
import { Bar, Empty, UnverifiedBanner } from '../ui'

const nineLabel = (nine: Tournament['rounds'][number]['nine']): string =>
  nine === 'front' ? 'Front 9' : nine === 'back' ? 'Back 9' : nine === 'full18' ? '18 holes' : 'Selected holes'

/**
 * The championship field, as it would fall today.
 *
 * The flights are seeded off the standings going into the final, so until the
 * week is played they are a projection and nothing more. Two things follow, and
 * both are about not misleading anybody out of a thousand dollars:
 *
 * Before a single point has been scored there is nothing to seed on — every
 * player is level, and an order drawn out of a twelve-way tie would look
 * authoritative while meaning nothing. That case says TBD and stops.
 *
 * Once there are points, the projection is worth showing, because the man on
 * the flight-one bubble is the person who most wants to see it. It is labeled
 * as of this moment, every time, in the heading and again underneath.
 */
function Flights({
  doc,
  flights,
  seeding,
  chFor,
  blockers,
  onSign,
  seedingComplete,
  spot,
  onSpot,
}: {
  doc: Tournament
  flights: { flight: { name: string }; playerIds: string[] }[]
  seeding: Standings
  /** The course handicap this round's card gives each player. */
  chFor: (pid: string) => number | undefined
  /** Cards whose missing signature is keeping this a projection. */
  blockers: OutstandingCard[]
  onSign?: (roundId: string) => void
  /** True once every card the seeding reads is signed — the word "final" is earned. */
  seedingComplete: boolean
  spot: string | null
  onSpot: (pid: string | null) => void
}) {
  const pointsOf = (pid: string): number =>
    seeding.rows.find((r) => r.playerId === pid)?.points ?? 0
  const decided = hasSeedingBasis(seeding)

  if (!decided) {
    return (
      <p className="px-4 pb-3 text-[13px] text-mute leading-snug">
        <b className="bc-name text-ink">Flights are TBD.</b> The field is seeded off the
        standings going into the final, into the flights Setup defines. Nothing has been
        played yet, so there is nothing to seed on.
      </p>
    )
  }

  return (
    <>
      {/*
        Named before the table, not after it. The flights are seeded off signed
        cards only, so an unsigned one means these are drawn from part of the
        week — and somebody on the Flight 1 bubble needs that said before he
        reads his own name, not underneath it.
      */}
      <SeedingBlocked doc={doc} cards={blockers} onSign={onSign} />
      {/*
        Two moments, two different sentences. Before the eighth signature these
        flights are a projection and must say so; after it they are the real
        draw, and the audit caught this screen still muttering "not final, will
        move with every round" at men whose flights had been settled for an
        hour. The word "final" is earned by `seedingComplete`, the same gate
        the money uses.
      */}
      {seedingComplete ? (
        <p className="px-4 pb-2 text-[13px] text-mute leading-snug">
          <b className="text-signal bc-label">The flights are set</b> — every card that
          seeds them is signed. This is the championship draw.
        </p>
      ) : (
        <p className="px-4 pb-2 text-[13px] text-mute leading-snug">
          <b className="text-signal bc-label">If the week ended right now</b> — seeded off
          the current standings. These are not final and will move with every round.
        </p>
      )}
      <ul className="divide-y divide-rule border-t border-rule">
        {flights.map(({ flight, playerIds }, fi) => {
          /*
           * The flight's tee time, off the group STAMPED with its number — the
           * one fact a player planning his Wednesday actually needs from this
           * screen, and until the audit it rendered nowhere outside Setup. The
           * admin may have reordered the slots, which is why this is a lookup
           * by stamp and not "first flight goes off first".
           */
          const champRound = doc.matches.find(
            (m) => m.config.kind === 'strokeNet' && m.config.flighted,
          )?.roundId
          const teeTime = doc.groups.find(
            (g) => g.roundId === champRound && (g.flight ?? 0) === fi + 1,
          )?.teeTime
          return (
          <li key={flight.name} className="px-4 py-3">
            <div className="flex items-baseline justify-between">
              <span className="bc-label">
                {flight.name}
                {teeTime && <span className="text-mute"> · {clockTime(teeTime)}</span>}
              </span>
              {/* "provisional" was still printed beside flights that were set. */}
              <span className="text-[12px] text-mute">
                {seedingComplete ? 'set' : 'provisional'}
              </span>
            </div>
            {playerIds.length === 0 ? (
              <p className="text-[13px] text-dim mt-1">Nobody seeded here yet.</p>
            ) : (
              <ul className="mt-1.5">
                {playerIds.map((pid) => {
                  const ch = chFor(pid)
                  return (
                    <li key={pid} className="py-0.5">
                      <div className="flex items-baseline justify-between gap-3 text-[13px]">
                        <span className="bc-name">
                          <NameTap doc={doc} pid={pid} spot={spot} onSpot={onSpot} />
                        </span>
                        <span className="text-mute tabular-nums shrink-0">
                          {pointsOf(pid)} pts
                        </span>
                      </div>
                      {/*
                        A starting score, not a list of holes.
                        
                        This is stroke play: the handicap is not spent hole by
                        hole against an opponent, it is simply where you begin
                        relative to par. Naming the pop holes here answered a
                        match-play question nobody is asking in this round, and
                        buried the one number that decides it.
                      */}
                      <div className="text-[12px] text-mute">
                        {ch == null ? null : <>CH {ch} · </>}
                        {ch == null ? (
                          <span className="text-dim">no handicap set</span>
                        ) : ch === 0 ? (
                          <>starts at <span className="bc-num text-ink">level</span></>
                        ) : (
                          <>
                            starts at <span className="bc-num text-ink">&minus;{ch}</span>
                          </>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </li>
          )
        })}
      </ul>
    </>
  )
}

function GettingThere({ travel, played }: { travel: string; played: boolean }) {
  /*
   * `useState`, not a derived value: the default is decided once at mount, so a
   * score arriving on the 8-second poll cannot slam the section shut while
   * somebody is reading the shuttle time. Preview unmounts on every tab switch,
   * which quietly gives the right refresh cadence for free.
   */
  const [open, setOpen] = useState(!played)
  return (
    <div className="border-b border-rule">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="bc-tap w-full bc-label bg-ink text-paper px-4 py-2 text-left flex items-center justify-between"
      >
        Getting there
        {/* The affordance: a header that toggles has to look like it might. */}
        <span aria-hidden="true" className="text-onink">
          {open ? '\u25b4' : '\u25be'}
        </span>
      </button>
      {open && (
        <ul className="px-4 py-3 space-y-1.5">
          {travel
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean)
            .map((line, i) => (
              <li key={i} className="text-sm leading-snug flex gap-2">
                <span className="text-signal shrink-0" aria-hidden="true">
                  &bull;
                </span>
                <span className="min-w-0">{withTailNumbers(line)}</span>
              </li>
            ))}
        </ul>
      )}
    </div>
  )
}

/**
 * A US tail number in a line of travel prose, turned into live tracking.
 *
 * The trip's facts arrive in the group thread as sentences, and the block
 * that holds them is deliberately free text — so the link is found rather
 * than configured: anything shaped like N563CB becomes a tap through to
 * FlightAware, which answers the only question anybody has about a tail
 * number ("where is it, and is it late"). Nothing else in the line changes.
 *
 * The pattern is the real FAA shape — N, then digits, then at most two
 * letters — so it cannot swallow an ordinary word that happens to start
 * with N.
 */
const TAIL = /\b(N\d{1,5}[A-Z]{0,2})\b/g

function withTailNumbers(line: string): React.ReactNode {
  const parts = line.split(TAIL)
  if (parts.length === 1) return line
  return parts.map((part, i) =>
    // split() puts every capture at an odd index.
    i % 2 === 1 ? (
      <a
        key={i}
        href={`https://www.flightaware.com/live/flight/${part}`}
        target="_blank"
        rel="noopener"
        className="bc-name text-signal underline underline-offset-2"
      >
        {part}
      </a>
    ) : (
      part
    ),
  )
}

/**
 * One massage slot — and, if it is going spare, the two taps that take it.
 *
 * Scott's ask: an open chair should be claimable where it is read, not by
 * texting the organiser. Tapping an open slot opens a picker already
 * holding your own name — Save books it — with the rest of the field in the
 * dropdown for the man booking on somebody's behalf. A slot you already
 * hold can be handed on or given back; one that is somebody else's is text,
 * because taking a chair out from under a man is a Setup-sized decision.
 */
function MassageSlot({
  doc,
  day,
  slot,
  me,
}: {
  doc: Tournament
  day: string
  slot: { time: string; table?: number; playerId: string | null }
  me: string
}) {
  const [open, setOpen] = useState(false)
  const [pick, setPick] = useState(me)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mine = slot.playerId === me
  const claimable = me !== '' && (!slot.playerId || mine)

  const save = async (playerId: string | null) => {
    setBusy(true)
    setError(null)
    try {
      await claimMassage(day, slot.time, playerId, slot.table)
      setOpen(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className="text-sm">
      {/*
        EVERY row is the same shape, booked or open — that uniformity is the
        fix. A first pass gave only the tappable rows the 44px-with-negative-
        margin treatment and left booked ones as plain text, so two rows that
        look identical measured differently and the column came out uneven
        wherever a name sat among the open slots. The tap area is still a full
        44px; the row is the height of its text.
      */}
      <button
        type="button"
        disabled={!claimable}
        onClick={() => {
          setPick(slot.playerId ?? me)
          setOpen((v) => !v)
        }}
        className="bc-tap -my-2.5 flex items-baseline gap-3 text-left w-full"
      >
        <span className="bc-num text-signal shrink-0">{clockTime(slot.time)}</span>
        {/* Which chair, when two run at once — the answer to "we booked the
            same time, did one of us lose it?" */}
        {slot.table != null && (
          <span className="text-[11px] text-mute shrink-0">T{slot.table}</span>
        )}
        {slot.playerId ? (
          <span className={mine ? 'bc-name' : ''}>{playerName(doc, slot.playerId)}</span>
        ) : (
          <span className={claimable ? 'text-pop' : 'text-mute'}>
            {claimable ? 'open — take it' : 'open'}
          </span>
        )}
      </button>
      {open && claimable && (
        <div className="flex flex-wrap items-center gap-2 py-1.5">
          <select
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            className="border-2 border-ink px-2 bc-tap bg-paper text-sm min-w-0 flex-1"
          >
            {doc.players
              .filter((p) => p.active)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
          </select>
          <button
            type="button"
            disabled={busy}
            onClick={() => void save(pick)}
            className="bc-tap bc-label px-3 bg-ink text-paper disabled:opacity-40"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
          {mine && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void save(null)}
              className="bc-tap bc-label px-3 border border-rule disabled:opacity-40"
            >
              Give it back
            </button>
          )}
          {error && <span className="text-[12px] text-signal w-full">{error}</span>}
        </div>
      )}
    </li>
  )
}

/**
 * One day of the week, folded or not.
 *
 * Twenty-seven holes a day means two and three rounds sharing a date, and nine
 * of them printed in one run put the round a man is standing on three screens
 * below two days of finished golf. One day is open at a time, so the screen is
 * four headings and the day that matters rather than a mile of scroll.
 *
 * Which one that is belongs to the parent, not here — an accordion cannot be
 * assembled out of sections that each decide for themselves. This component
 * only reports the tap.
 */
function Day({
  label,
  courses,
  weather,
  open,
  today,
  onToggle,
  scrollRef,
  children,
}: {
  label: string
  /** The courses played that day, so a folded day still says what it is. */
  courses: string
  /**
   * The day's forecast, carried on a folded header — the temperature and the
   * wind are why somebody opens this screen the night before, and a day you
   * have to unfold to see them is a day you compare by memory.
   */
  weather: ForecastDay | undefined
  open: boolean
  today: boolean
  onToggle: () => void
  /** Set by the parent on the day it has just been asked to open. */
  scrollRef?: (el: HTMLDivElement | null) => void
  children: React.ReactNode
}) {
  return (
    <div ref={scrollRef} data-week-day={label} className="border-b border-rule">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="bc-tap w-full bg-ink text-paper px-4 py-2 text-left flex items-baseline gap-2"
      >
        <span className="bc-label shrink-0">{label}</span>
        {today && (
          <span className="bc-label bc-chip shrink-0 bg-paper text-ink px-1.5">Today</span>
        )}
        <span className="text-[12px] text-onink truncate min-w-0">{courses}</span>
        {weather && (
          <span className="text-[12px] text-onink shrink-0 tabular-nums flex items-center gap-1">
            <SkyGlyph code={weather.code} />
            {Math.round(weather.tempMax)}°
            <WindArrow deg={weather.windDir} strong={weather.windMax >= 15} />
            {Math.round(weather.windMax)} {compass(weather.windDir)}
          </span>
        )}
        {/* The affordance: a header that toggles has to look like it might. */}
        <span aria-hidden="true" className="text-onink ml-auto pl-2 shrink-0">
          {open ? '▴' : '▾'}
        </span>
      </button>
      {open && children}
    </div>
  )
}


/**
 * What a man is getting, said the way the format spends it.
 *
 * Quota rounds start the handicap as points; pairing formats pop on holes; a
 * zero says WHY it is zero (level, or giving) because "no strokes" got the app
 * accused of bad math from the actual first tee. One component, both layouts —
 * the stacked table and the paired columns — so the wording cannot drift.
 */
function StrokeNote({
  ctx,
  contests,
  quota,
  quotaBase,
  quotaFor,
  groupFramed,
  holes,
  pid,
}: {
  ctx: ReturnType<typeof buildContext> | null
  contests: Tournament['pairings']
  quota: { quotaFormula: { pointsPerPar: number } } | null
  quotaBase: number
  quotaFor: (pid: string) => number
  groupFramed: boolean
  holes: { number: number }[]
  pid: string
}) {
  const pops = ctx?.pops[pid] ?? []
  const popHoles = holes
    .map((h, i) => ({ h, n: pops[i] ?? 0 }))
    .filter((x) => x.n > 0)
  const total = popHoles.reduce((sum, x) => sum + x.n, 0)
  /*
   * Just the number he starts on. This said "starts on 3 of 18", and the
   * "of 18" is the same for all twelve men in the round — the ranking is
   * relative, so the base adds nothing and cost a third of the column.
   */
  if (quota)
    return (
      <>
        <span className="text-dim">starts on </span>
        <span className="bc-num text-ink">{quotaBase - quotaFor(pid)}</span>
      </>
    )
  if (total === 0) {
    if (groupFramed) return <span className="text-dim">level</span>
    const pairing = contests.find((x) => x.sides.some((sd) => sd.playerIds.includes(pid)))
    if (!pairing) return <span className="text-dim">no strokes</span>
    const oppTotal = pairing.sides
      .filter((sd) => !sd.playerIds.includes(pid))
      .flatMap((sd) => sd.playerIds)
      .reduce(
        (mx, op) => Math.max(mx, (ctx?.pops[op] ?? []).reduce((a, b) => a + b, 0)),
        0,
      )
    return oppTotal > 0 ? (
      <span className="text-dim">gives {oppTotal}</span>
    ) : (
      <span className="text-dim">level</span>
    )
  }
  return (
    <>
      <span className="bc-num text-ink">{total}</span>
      {' on '}
      {popHoles.map((x) => `${x.h.number}${x.n > 1 ? `×${x.n}` : ''}`).join(', ')}
    </>
  )
}


/**
 * One course session's masthead — Scott's redesign of the week's headers.
 *
 * The course is the thing somebody scanning the week is looking for, so it is
 * the big line; the GAME is the second question and gets the signal color;
 * the date and the match numbers are bookkeeping and live small on the right.
 * Two columns so it stays two lines however long the course name runs.
 */
function SessionHead({
  course,
  game,
  day,
  nums,
  forFun,
}: {
  course: string
  game: string
  day: string
  nums: string
  forFun?: boolean
}) {
  return (
    <div className="border-t-[3px] border-ink mx-4 mt-5 mb-1 pt-2 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="bc-display text-xl leading-tight">{course}</h2>
        {(game || forFun) && (
          <p className="bc-label text-signal mt-1 text-[14px]">
            {game}
            {forFun && (
              <span className="bc-chip bg-wash text-mute px-1.5 ml-2 text-[11.5px]">For fun</span>
            )}
          </p>
        )}
      </div>
      <div className="text-right shrink-0 pt-0.5">
        {day && <span className="text-base block">{day}</span>}
        {/* The trailing space is load-bearing: textContent joins this span to
            whatever follows, and "Match 3" against a "9:00am" tee time reads
            back as Match 39 to anything scanning the page. */}
        {nums && <span className="text-[12px] text-mute block mt-0.5">{nums}&nbsp;</span>}
      </div>
    </div>
  )
}

/**
 * A name you can tap, and the tap follows the man across the whole week.
 *
 * "Where am I, all week?" used to be answered by reading every foursome table
 * for your own name. Tapping any name now flashes and then holds a highlight
 * on that name everywhere it appears in the open days — Scott's ask, worded as
 * "flash and then highlight me everywhere in the card for the week". Tapping
 * the lit name again, or any other name, moves it on.
 *
 * The button keeps its 44px hit area through negative margins, so the dense
 * foursome tables stay dense: the target is thumb-sized, the row is not.
 */
function NameTap({
  doc,
  pid,
  spot,
  onSpot,
}: {
  doc: Tournament
  pid: string
  spot: string | null
  onSpot: (pid: string | null) => void
}) {
  const lit = spot === pid
  return (
    <button
      type="button"
      aria-pressed={lit}
      data-week-name={pid}
      onClick={() => onSpot(lit ? null : pid)}
      className={`bc-tap -my-3 inline-flex items-center text-left align-baseline ${
        lit ? 'week-spot' : ''
      }`}
    >
      {/*
        The paint goes on this inner span, never on the button: the button is
        44px tall for the thumb's sake, and painting IT swallowed the row
        below. The span is the height of the name, so the highlight is too —
        a translucent, rounded wash of the signal color, with the text keeping
        its own ink so every theme stays legible under the tint.
      */}
      <span className={lit ? 'rounded bg-signal/50 px-1.5 -mx-1.5 py-0.5 -my-0.5' : undefined}>
        {playerName(doc, pid)}
      </span>
    </button>
  )
}

/**
 * Everything one nine asks of one foursome, in one column.
 *
 * Tee time and weather, then the contests with each man's strokes. No CH
 * column — the roster carries it on The field, and in a 180px column the
 * number crowded out the one thing this table is for, which is who is giving
 * what to whom.
 */
function NineColumn({
  doc,
  round,
  group,
  weather,
  scores,
  spot,
  onSpot,
  view,
}: {
  doc: Tournament
  round: Tournament['rounds'][number]
  group: Tournament['groups'][number]
  weather: Parameters<typeof hourFor>[0]
  scores: Parameters<typeof buildContext>[3]
  spot: string | null
  onSpot: (pid: string | null) => void
  /** The scored week, so a finished match can say who won right here. */
  view?: TournamentView
}) {
  const course = courseOf(doc, round)
  const match = doc.matches.find((m) => m.roundId === round.id)
  const holes = course ? holesFor(course, round.nine) : []
  const ctx = course && match ? buildContext(doc, round, match, scores) : null
  const quota = match?.config.kind === 'quota' ? match.config : null
  const groupFramed =
    match != null &&
    match.config.kind !== 'quota' &&
    match.config.kind !== 'strokeNet' &&
    match.config.netBasis === 'relativeToLowInGroup'
  const quotaBase = quota ? quota.quotaFormula.pointsPerPar * holes.length : 0
  const quotaFor = (pid: string): number =>
    quota
      ? quotaTarget(
          quota,
          holes.length,
          ctx?.handicaps[pid]?.courseHandicap ?? 0,
          ctx?.handicaps[pid]?.quotaTarget ?? null,
        )
      : 0
  const contests = doc.pairings.filter(
    (p) => p.groupId === group.id && (!match || p.matchId === match.id),
  )
  const order = contestOrder(group.playerIds, contests)
  const hour = hourFor(weather ?? undefined, round.date, group.teeTime)

  /*
   * Who won, said where everybody keeps looking.
   *
   * Scott's observation from the property: the group reads THIS screen all
   * week, so a finished match has to say its result here, not only on the
   * Board. Once the match is final — every card signed — the strokes column
   * has done its job and the result takes its place: the winning side's
   * first row carries the margin, a halve says AS on both, and the strokes
   * stay put on every match still out.
   */
  const finished =
    match && view?.finalMatchIds.has(match.id) ? view.byMatch[match.id] : null
  const resultFor = (pid: string): { label: string; won: boolean } | null => {
    if (!finished) return null
    const pairing = contests.find((x) => x.sides.some((sd) => sd.playerIds.includes(pid)))
    if (!pairing) return null
    const side = pairing.sides.find((sd) => sd.playerIds.includes(pid))!
    if (side.playerIds[0] !== pid) return null
    const mine = finished.tabs.find((t) => t.unitId === side.id)
    const other = pairing.sides.find((sd) => sd.id !== side.id)
    const theirs = other ? finished.tabs.find((t) => t.unitId === other.id) : undefined
    if (!mine || !theirs) return null
    if (mine.rankValue === theirs.rankValue) return { label: 'AS', won: false }
    // The chip on a closeout already says "Won 3 & 2"; the renderer prefixes
    // its own "won", so strip the chip's or the week prints "won won 3 & 2".
    if (mine.rankValue > theirs.rankValue)
      return { label: mine.chip.primary.replace(/^won\s*/i, ''), won: true }
    return null
  }

  return (
    <div className="min-w-0">
      <div className="flex items-baseline gap-1.5 flex-wrap">
        <span className="bc-num text-signal">{clockTime(group.teeTime)}</span>
        {hour && (
          <span className="text-[11px] text-mute tabular-nums flex items-center gap-1">
            <SkyGlyph code={hour.code} />
            {Math.round(hour.temp)}°
            <WindArrow deg={hour.dir} strong={hour.wind >= 15} />
            {Math.round(hour.wind)}
          </span>
        )}
      </div>
      {/*
        The same vocabulary as the scorer's card: a thin v-line between the two
        SIDES of one match, a plain gap between two matches that have nothing
        to do with each other. Without it a 2v2 column was four names in a
        stack and nothing said who was carrying whom — reported from the
        phone as "confusing on what the matches are".
      */}
      <table className="mt-1 w-full text-xs">
        <tbody>
          {order.playerIds.map((pid, rowIndex) => (
            <Fragment key={pid}>
              {/* A four-ball foursome is two matches stacked; py-1 read as one
                  block of eight names. The v-line inside a match is tight,
                  so the gap BETWEEN matches has to be plainly bigger. */}
              {order.contestStarts.has(rowIndex) && (
                <tr aria-hidden="true">
                  <td colSpan={2} className="py-2.5" />
                </tr>
              )}
              {order.sideStarts.has(rowIndex) && (
                <tr aria-hidden="true" data-side-break="">
                  <td colSpan={2} className="py-0">
                    <div className="flex items-center gap-1.5">
                      <span className="h-px flex-1 bg-rule" />
                      <span className="text-[10px] font-semibold uppercase text-mute leading-none">
                        v
                      </span>
                      <span className="h-px flex-1 bg-rule" />
                    </div>
                  </td>
                </tr>
              )}
              <tr className="align-baseline">
                <td className="bc-name py-0.5 pr-1.5 whitespace-nowrap w-px">
                  <NameTap doc={doc} pid={pid} spot={spot} onSpot={onSpot} />
                </td>
                <td className="py-0.5 text-mute text-right">
                  {(() => {
                    const res = finished ? resultFor(pid) : null
                    if (finished) {
                      return res ? (
                        <span
                          className={
                            res.won ? 'bc-num text-ink' : 'bc-num text-dim'
                          }
                        >
                          {res.label.toLowerCase() === 'as' ? 'AS' : `won ${res.label.toLowerCase()}`}
                        </span>
                      ) : null
                    }
                    return (
                      <StrokeNote
                        ctx={ctx}
                        contests={contests}
                        quota={quota}
                        quotaBase={quotaBase}
                        quotaFor={quotaFor}
                        groupFramed={groupFramed}
                        holes={holes}
                        pid={pid}
                      />
                    )
                  })()}
                </td>
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * A day at one course, one row per foursome, the two nines side by side.
 *
 * The foursome walks all eighteen; the draw re-pairs them at the turn. Stacked
 * sections made that man scroll a screen to find his own afternoon. Here his
 * morning and his afternoon are one glance — front nine left, back nine right,
 * tee times on both.
 *
 * Only offered when every front foursome reappears intact on the back, which
 * the caller checks — four men split across two afternoon groups have no
 * single row to share.
 */
function PairedNines({
  doc,
  front,
  back,
  weather,
  scores,
  spot,
  onSpot,
  view,
}: {
  doc: Tournament
  front: Tournament['rounds'][number]
  back: Tournament['rounds'][number]
  weather: Parameters<typeof hourFor>[0]
  scores: Parameters<typeof buildContext>[3]
  spot: string | null
  onSpot: (pid: string | null) => void
  view?: TournamentView
}) {
  const groupsOf = (r: Tournament['rounds'][number]) =>
    doc.groups.filter((g) => g.roundId === r.id).sort((a, b) => a.index - b.index)
  const frontGroups = groupsOf(front)
  const backGroups = groupsOf(back)
  const partnerOf = (g: Tournament['groups'][number], i: number) =>
    backGroups.find((b) => {
      const a = new Set(g.playerIds)
      return b.playerIds.length === g.playerIds.length && b.playerIds.every((x) => a.has(x))
    }) ?? backGroups[i]

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 px-3 pt-1">
        <span className="bc-label text-mute">{nineLabel(front.nine)}</span>
        <span className="bc-label text-mute">{nineLabel(back.nine)}</span>
      </div>
      <ul className="divide-y divide-rule border-t border-rule mt-1">
        {frontGroups.map((g, i) => {
          const b = partnerOf(g, i)
          return (
            <li key={g.id} className="px-3 py-2.5 grid grid-cols-2 gap-3">
              <NineColumn doc={doc} round={front} group={g} weather={weather} scores={scores} spot={spot} onSpot={onSpot} view={view} />
              {b ? (
                <NineColumn doc={doc} round={back} group={b} weather={weather} scores={scores} spot={spot} onSpot={onSpot} view={view} />
              ) : (
                <span className="text-[12px] text-mute">No back-nine draw yet.</span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function Preview({
  doc,
  header,
  onSign,
}: {
  doc: Tournament
  /** The mode toggle, rendered under this screen's own masthead. */
  header?: React.ReactNode
  /** Jump to the Score tab on a round, so an unsigned card can be signed. */
  onSign?: (roundId: string) => void
}) {
  const scores = useStore(mergedScores)
  // The championship is not drawn, it is seeded — so the week view has to score
  // the tournament to know who is in which flight.
  const confirmations = useStore(mergedConfirmations)
  const view = computeAll(doc, scores, confirmations)
  const blockers = seedingBlockers(doc, scores, confirmations)
  // Read once, so the day sections and the Today chip cannot disagree about
  // which day it is — even across the midnight of the 25th.
  const today = todayLocal()
  /*
   * Asked for once on arrival. The server caches it for half an hour, so twelve
   * men opening the week the night before make one call to the forecaster
   * between them, and a phone with no signal keeps what it already had.
   */
  const weather = useStore((st) => st.weather)
  useEffect(() => {
    void fetchWeather()
  }, [])
  // In play order first: `roundDays` orders within a day by `seq`, and a round
  // added mid-trip carries the highest one — so a morning nine added on the day
  // printed below that afternoon's round.
  const days = roundDays(inPlayOrder(doc.rounds))
  /*
   * Which days are open — today and everything still to come, by default, so
   * the week reads whole before the trip and folds up behind itself as it is
   * played. Each header toggles its own day and only its own: Scott's ruling,
   * after the one-day accordion made Tuesday night's planning a tap per day.
   *
   * Seeded once, at mount, for the reason `GettingThere` gives below: a score
   * landing on the 8-second poll must not be able to move the page under
   * somebody reading it. Preview unmounts on every tab switch, so coming back
   * to the screen re-derives the days — which is the right cadence, and the
   * reason this does not need to watch the clock.
   */
  const [openDays, setOpenDays] = useState<ReadonlySet<string>>(
    () => new Set(defaultOpenDays(days, today)),
  )
  /*
   * The day a tap just acted on, and where its section is on the page.
   *
   * Folding a day away deletes content ABOVE the header that was tapped, so the
   * page slides up underneath the reader's thumb: with Sunday open and scrolled
   * to the bottom of it, tapping Monday leaves Monday's heading somewhere above
   * the top of the screen, and the first thing you have to do with the day you
   * just opened is scroll back up to find the start of it. Reported from a
   * phone.
   *
   * So the section that was tapped is brought back to the top edge. It is a
   * separate piece of state from `openDay` rather than an effect on it, because
   * this must happen on a TAP and never on arrival — the default day opens
   * itself, and scrolling to that would yank the screen past the masthead and
   * the travel note before anybody had touched anything.
   */
  const [tapped, setTapped] = useState<string | null>(null)
  /*
   * The spotlighted man, week-wide. One value, not a flag per section: the
   * point of the tap is that Cramer lights up in EVERY foursome at once, so
   * holding it anywhere lower would make the sections disagree.
   *
   * Seeded with WHOEVER IS SIGNED IN — Scott's call. Opening the week answers
   * "where am I, all week" before a single tap, and because Preview remounts
   * on every visit to the tab, the flash runs on arrival each time, which is
   * also what teaches twelve people that names are tappable at all. Tapping
   * your own lit name clears it (for the screenshot going to the thread);
   * tapping anyone else moves it to them.
   */
  const me = useStore((s) => s.session?.playerId ?? '')
  const [spot, setSpot] = useState<string | null>(() => me || null)
  /*
   * The simulator takes over the whole screen rather than interleaving with
   * the schedule: picking twenty results between dense foursome tables would
   * bury both. Same masthead slot, one back button, no other navigation.
   */
  const sections = useRef(new Map<string, HTMLDivElement>())
  useLayoutEffect(() => {
    if (tapped === null) return
    /*
     * Before paint, and instantly. The section has just changed height, and an
     * animation started from the old geometry glides confidently to the wrong
     * place — the same reason the card scrolls to a birdie with `auto`.
     */
    sections.current.get(tapped)?.scrollIntoView({ block: 'start', behavior: 'auto' })
    setTapped(null)
  }, [tapped])

  if (doc.rounds.length === 0) {
    return (
      <>
        <Bar title="The week" />
        <Empty>Nothing scheduled yet. An admin can build it under Setup.</Empty>
      </>
    )
  }

  const unverified = doc.courses.filter((c) => !c.verified)

  return (
    <>
      <Bar
        kicker={`${doc.name} · ${doc.venue}`}
        title="The week"
        /*
         * The men who are DRAWN, not the men on the roster.
         *
         * Marking somebody inactive left the header saying eleven while every
         * day below it printed twelve, him included — the header describing one
         * set of men and the golf another, on the same screen. When they
         * disagree the header says so, because that disagreement is the thing
         * worth knowing: somebody is in the draw who should not be, or the
         * other way round.
         */
        sub={(() => {
          const drawn = new Set(doc.groups.flatMap((g) => g.playerIds)).size
          const active = doc.players.filter((p) => p.active).length
          const rounds = `${doc.rounds.length} round${doc.rounds.length === 1 ? '' : 's'}`
          if (drawn === 0) return `${rounds} · ${active} players · no draw yet`
          return drawn === active
            ? `${rounds} · ${drawn} players`
            : `${rounds} · ${drawn} drawn, ${active} on the roster`
        })()}
      />
      {header}
      {/*
        The yearbook goes above the schedule, and only once the week is over.
        Until the championship is signed it renders nothing at all — a
        half-written yearbook would push the days, which is the thing anybody
        opens this screen for mid-trip, below the fold for no gain.
      */}
      <Yearbook doc={doc} scores={scores} view={view} me={me} />
      {/*
        The simulator's front door used to be here, on top of the week. It
        moved to the Board's Standings tab — Scott's call, and the right one:
        the simulator answers "where would I finish", which is a standings
        question, asked by somebody already looking at the standings.
      */}
      {unverified.length > 0 && <UnverifiedBanner courseName={unverified[0]!.name} />}

      {/*
        The trip, above the golf — and out of the way once the golf exists.

        On the 22nd of August nobody opening this wants the stroke index
        warning — they want to know what time the plane leaves. The moment the
        FIRST score of the week lands, the answer flips: everyone opening this
        screen is here for the rounds, and the flight details are a solved
        problem taking up half the fold. So the section starts expanded before
        play and collapsed after, and the header always toggles it by tap.

        Collapsed, not removed, and that is deliberate where "gone" would have
        been easier: the same block holds the trip HOME — wheels up the evening
        of the 27th — which is exactly the line somebody needs on the last day,
        long after the first score made the section "stale". One header row is
        the cost of never having deleted the return flight.
      */}
      {doc.settings.travel?.trim() && <GettingThere travel={doc.settings.travel} played={scores.length > 0} />}

      {/*
        Where the week sleeps. An external site cannot carry the app's own
        chrome, so the "back button" is the view it opens in: target=_blank
        from an installed PWA opens the in-app browser sheet with its Done
        control, and the app is still exactly where it was underneath.
      */}
      <a
        href="https://www.bandonwoods.com/"
        target="_blank"
        rel="noopener"
        className="bc-tap bc-label w-full py-3 px-4 flex items-center justify-between border-b border-rule"
      >
        <span>Where we&rsquo;re staying</span>
        <span className="text-mute text-[13px] normal-case tracking-normal">
          bandonwoods.com ›
        </span>
      </a>

      <p className="px-4 py-3 text-[13px] text-mute leading-snug border-b border-rule">
        Check your own pop holes against the card in the pro shop. A wrong stroke index
        is invisible once play starts and quietly changes every net score for the week.
      </p>

      {/*
        The week, day by day, each one folding on a tap of its header.

        The days come from `roundDays` rather than from a sort here, because
        "which of these is past" is the rule that decides what is folded when
        the screen opens, and a rule about the calendar wants a test that can
        hand it a date. See `select.days.test.ts`.
      */}
      {days.map((d) => (
        <Day
          key={d.date || 'undated'}
          label={d.day || 'Date not set'}
          courses={[
            ...new Set(d.rounds.map((r) => courseOf(doc, r)?.name).filter(Boolean)),
          ].join(' · ')}
          weather={dayFor(weather ?? undefined, d.date)}
          open={openDays.has(d.date)}
          today={d.date === today}
          onToggle={() => {
            setOpenDays((cur) => {
              const next = new Set(cur)
              if (next.has(d.date)) next.delete(d.date)
              else next.add(d.date)
              return next
            })
            setTapped(d.date)
          }}
          scrollRef={(el) => {
            if (el) sections.current.set(d.date, el)
            else sections.current.delete(d.date)
          }}
        >
          {/*
            The day, before the golf. Wind first in spirit if not in order —
            at Bandon it is the course, and it is the reason anybody opens this
            screen the night before.
          */}
          {(() => {
            const day = dayFor(weather ?? undefined, d.date)
            if (!day) return null
            return (
              <p className="px-4 py-2 text-[13px] bg-wash border-b border-rule flex items-center gap-1.5 flex-wrap">
                <SkyGlyph code={day.code} />
                <span>{longWeather(day)}</span>
              </p>
            )
          })()}
          {(() => {
            /*
             * The day's own logistics — dinner, massages, the departure clock —
             * under the weather and above the golf. This is the screen a man
             * reads the night before and between nines, and "when is dinner
             * tonight" belongs inside tonight, not in a trip-wide blurb that
             * collapsed when the first score landed.
             */
            const lines = (doc.settings.dayNotes?.[d.date] ?? '')
              .split('\n')
              .map((l) => l.trim())
              .filter(Boolean)
              /*
               * Dinner is pulled OUT of this list and rendered at the foot of
               * the day instead — Scott's call: by evening the lineups are
               * spent information and "when is dinner" is the whole question,
               * so it gets its own bold block after the golf rather than a
               * bullet above it.
               */
              .filter((l) => !/dinner/i.test(l))
            if (lines.length === 0) return null
            return (
              <ul className="px-4 py-2 text-[13px] text-mute border-b border-rule space-y-0.5">
                {lines.map((l) => (
                  <li key={l} className="flex gap-2">
                    <span aria-hidden="true" className="text-dim">·</span>
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            )
          })()}
          {(() => {
            /*
             * The massage sheet, where the tee times live. Sorted by time
             * whatever order Setup typed them in, your own slot in the same
             * bold every other screen uses for you, and an unassigned slot
             * says "open" — the answer to "can I still get one" without a
             * text to the thread.
             */
            const all = [...(doc.settings.massages?.[d.date] ?? [])]
              .filter((x) => x.time)
              .sort((a, b) =>
                a.time < b.time ? -1 : a.time > b.time ? 1 : (a.table ?? 1) - (b.table ?? 1),
              )
            if (all.length === 0) return null
            /*
             * A slot whose hour has gone is history — Scott's ask. On any day
             * but today every slot is settled, so the whole sheet folds to one
             * line; today it keeps the chairs still to come and says how many
             * have been and gone. A booked past slot is not erased, just
             * folded: the record of who had 8:15 survives in Setup.
             */
            const now = new Date()
            const nowMin = now.getHours() * 60 + now.getMinutes()
            const isToday = d.date === todayLocal()
            const past = (x: { time: string }) => {
              const [h, m] = x.time.split(':').map(Number)
              return (h ?? 0) * 60 + (m ?? 0) < nowMin
            }
            const done = d.date < todayLocal() ? all : isToday ? all.filter(past) : []
            const slots = all.filter((x) => !done.includes(x))
            if (slots.length === 0) {
              const taken = done.filter((x) => x.playerId).length
              return (
                <div data-massages="" className="px-4 py-1.5 border-b border-rule">
                  <span className="bc-label text-mute">
                    Massages — done ({taken} of {done.length} taken)
                  </span>
                </div>
              )
            }
            return (
              <div data-massages="" className="px-4 py-2 border-b border-rule">
                <span className="bc-label text-mute block">
                  Massages
                  {done.length > 0 && (
                    <span className="normal-case tracking-normal">
                      {' '}
                      · {done.length} done
                    </span>
                  )}
                </span>
                {/* Columns of four, not a ladder of twelve: the sheet reads
                    down then across, so a full day stays three columns tall
                    instead of scrolling the golf off the screen. */}
                <ul
                  className="mt-2 grid grid-flow-col gap-x-6 gap-y-2 justify-start"
                  style={{ gridTemplateRows: `repeat(${Math.min(4, slots.length)}, auto)` }}
                >
                  {slots.map((slot, i) => (
                    <MassageSlot
                      key={`${slot.time}-${slot.table ?? 1}-${i}`}
                      doc={doc}
                      day={d.date}
                      slot={slot}
                      me={me}
                    />
                  ))}
                </ul>
              </div>
            )
          })()}
          {(() => {
            /*
             * One section per course session, not per nine.
             *
             * A day at Bandon is "Pacific Dunes, front then back" — one place,
             * two tee times, usually two different draws. Rendering each nine
             * as its own full section printed the course name twice and made
             * eleven headings out of seven sessions. Consecutive rounds on the
             * same course within a day share a heading; the nines keep their
             * own draw, format and tables beneath it. Only CONSECUTIVE rounds
             * merge — a morning nine somewhere else between two halves of a
             * course is a different session and stays one.
             */
            const chunks: (typeof d.rounds)[] = []
            for (const r of d.rounds) {
              const open = chunks[chunks.length - 1]
              if (open && open[0]!.courseId === r.courseId) open.push(r)
              else chunks.push([r])
            }
            /*
             * A front-and-back pair whose foursomes survive the turn intact
             * goes side by side — see PairedNines. Anything else (a lone
             * round, an 18, a redraw that split a group) stacks as before.
             */
            const pairable = (chunk: typeof d.rounds): boolean => {
              if (chunk.length !== 2) return false
              const [a, b] = chunk as [(typeof d.rounds)[number], (typeof d.rounds)[number]]
              if (a.nine === 'full18' || b.nine === 'full18') return false
              const ga = doc.groups.filter((g) => g.roundId === a.id)
              const gb = doc.groups.filter((g) => g.roundId === b.id)
              if (ga.length === 0 || ga.length !== gb.length) return false
              return ga.every((g) => {
                const men = new Set(g.playerIds)
                return gb.some(
                  (h) => h.playerIds.length === g.playerIds.length && h.playerIds.every((x) => men.has(x)),
                )
              })
            }
            return chunks.map((chunk) => {
              if (pairable(chunk)) {
                const [front, back] = chunk as [(typeof d.rounds)[number], (typeof d.rounds)[number]]
                const course = courseOf(doc, front)
                const fm = doc.matches.find((m) => m.roundId === front.id)
                const bm = doc.matches.find((m) => m.roundId === back.id)
                /* One line for the day when the nines share a format; both
                   named when the turn changes the game. */
                const fmt = formatName(fm?.config)
                const bfmt = formatName(bm?.config)
                const nums = [fm, bm]
                  .map((m) => m && matchNumber(m))
                  .filter(Boolean)
                  .map((x) => `Match ${x}`)
                  .join(' & ')
                return (
                  <div key={front.id}>
                    <SessionHead
                      course={course?.name ?? front.label}
                      game={fmt ? (fmt === bfmt ? fmt : `${fmt}, then ${bfmt}`) : ''}
                      day={roundDay(front.date)}
                      nums={nums}
                      forFun={Boolean(fm && !matchCounts(fm))}
                    />
                    <PairedNines
                      doc={doc}
                      front={front}
                      back={back}
                      spot={spot}
                      onSpot={setSpot}
                      weather={weather ?? undefined}
                      scores={scores}
                      view={view}
                    />
                  </div>
                )
              }
              return chunk.map((round, nthOfCourse) => {
            const course = courseOf(doc, round)
            const match = doc.matches.find((m) => m.roundId === round.id)
            const ctx = course && match ? buildContext(doc, round, match, scores) : null

            /*
             * Quota spends the handicap before the round rather than during it.
             *
             * The target is what the engine will score against, derived the same
             * way — an explicit per-player figure from the scorecard if there is
             * one, otherwise points-per-par across the holes played, less the
             * course handicap. Recomputing it here rather than inventing a
             * display rule keeps the sheet and the scoring in step.
             */
            const groups = doc.groups
              .filter((g) => g.roundId === round.id)
              .sort((a, b) => a.index - b.index)

            /*
             * The championship has no draw to show.
             *
             * Its foursomes are the flights, and the flights come off the
             * standings walking to the first tee — so whatever pairings sit in
             * the document for this round are a placeholder, and printing them
             * would tell twelve people they are playing with the wrong three.
             */
            const flighted =
              match?.config.kind === 'strokeNet' && match.config.flighted
                ? view.flights
                : null

            const shared = chunk.length > 1
            return (
              <div key={round.id}>
                {shared ? (
                  nthOfCourse === 0 && (
                    <SessionHead
                      course={course?.name ?? round.label}
                      game=""
                      day={roundDay(round.date)}
                      nums=""
                    />
                  )
                ) : (
                  <SessionHead
                    course={course?.name ?? round.label}
                    game={[
                      formatName(match?.config),
                      round.nine === 'full18' ? '' : nineLabel(round.nine),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    day={roundDay(round.date)}
                    nums={
                      match && matchNumber(match) ? `Match ${matchNumber(match)}` : ''
                    }
                    forFun={Boolean(match && !matchCounts(match))}
                  />
                )}
                {shared && (
                  <>
                    <h3 className="bc-label text-mute px-4 pt-2">{nineLabel(round.nine)}</h3>
                    {/* The head said the course and the day; this line owns
                        what changes at the turn — the match and its game. */}
                    <p className="px-4 pt-0.5 pb-2 text-[13px] text-mute">
                      {match
                        ? [matchNumber(match) && `Match ${matchNumber(match)}`, formatName(match.config)]
                            .filter(Boolean)
                            .join(' · ')
                        : ''}
                      {match && !matchCounts(match) && (
                        <span className="bc-label bc-chip ml-2 bg-wash text-mute px-1.5">
                          For fun
                        </span>
                      )}
                    </p>
                  </>
                )}

                {flighted && (
                  <Flights
                    doc={doc}
                    flights={flighted}
                    spot={spot}
                    onSpot={setSpot}
                    seeding={view.seeding}
                    chFor={(pid) => ctx?.handicaps[pid]?.courseHandicap}
                    blockers={view.seedingComplete ? [] : blockers}
                    onSign={onSign}
                    seedingComplete={view.seedingComplete}
                  />
                )}

                {/*
                  The same foursome cell the paired days use — Scott's call:
                  a lone nine (Trails Monday's four-ball) was rendering the
                  old wide table with a CH column while every other session
                  wore the tight Sunday format, and one screen should not
                  speak two dialects. `NineColumn` already knows every format
                  this round could carry.
                */}
                {flighted ? null : groups.length === 0 ? (
                  <p className="px-4 pb-3 text-[13px] text-mute">
                    No draw yet — generate the pairings under Setup.
                  </p>
                ) : (
                  <ul className="divide-y divide-rule border-t border-rule mt-1">
                    {groups.map((g) => (
                      <li key={g.id} className="px-3 py-2.5">
                        <NineColumn
                          doc={doc}
                          round={round}
                          group={g}
                          weather={weather ?? undefined}
                          scores={scores}
                          spot={spot}
                          onSpot={setSpot}
                          view={view}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })
          })
          })()}
          {(() => {
            const dinner = (doc.settings.dayNotes?.[d.date] ?? '')
              .split('\n')
              .map((l) => l.trim())
              .filter((l) => /dinner/i.test(l))
            if (dinner.length === 0) return null
            // A quarter-strength wash of the ink, not the full slab — the
            // first cut of this shouted. The ink text keeps its own color on
            // the tint, so every theme stays legible.
            return (
              <div data-dinner="" className="mx-4 my-3 bg-ink/25 px-4 py-3">
                <span className="bc-label text-mute block">Dinner</span>
                {dinner.map((l) => (
                  <p key={l} className="bc-name text-lg leading-snug mt-1">
                    {l}
                  </p>
                ))}
              </div>
            )
          })()}
        </Day>
      ))}

      <p className="px-4 py-6 text-[13px] text-mute leading-snug">
        CH is the course handicap that player&rsquo;s tee produces — it already accounts
        for playing a harder or easier set. Strokes shown are the ones that count in
        that round&rsquo;s format, which is why they differ between a match and the
        championship. Quota rounds show a starting points total instead: the handicap
        is spent there and then, and no hole carries a stroke.
      </p>
    </>
  )
}
